import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'; // Added useEffect
import {
  VStack,
  HStack,
  Box,
  Text,
  Button,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Select,
  useDisclosure,
  useColorModeValue,
  useToast, // Import useToast
  Badge,
  Divider,
  Tooltip,
  Alert,
  AlertIcon,
  List,
  ListItem,
  Heading,
  Collapse, // Added for expand/collapse
  Icon // Added for custom icons if needed
} from '@chakra-ui/react';
import {
  PlusCircle,
  Pencil,
  Trash,
  BookOpen,
  Article,
  Globe,
  LinkSimple,
  CaretDown, // Added for expand/collapse
  CaretUp, // Added for expand/collapse
  FileText, // For DOI
  Barcode // For ISBN
} from '@phosphor-icons/react';
import { detectCitationMarkers } from '../../../utils/citationDetectionUtils';
import { useCreateSite } from '../../../context/CreateSiteContext'; // Import context hook

/**
 * Citation types and their metadata
 */
const citationTypes = [
  { value: 'book', label: 'Book', icon: BookOpen, fields: ['title', 'author', 'year', 'publisher', 'pages', 'isbn'] },
  { value: 'article', label: 'Article', icon: Article, fields: ['title', 'author', 'journal', 'year', 'volume', 'issue', 'pages', 'doi'] },
  { value: 'website', label: 'Website', icon: Globe, fields: ['title', 'author', 'url', 'accessDate'] },
  { value: 'manual', label: 'Manual', icon: Pencil, fields: ['title', 'author', 'description'] },
  { value: 'other', label: 'Other', icon: LinkSimple, fields: ['title', 'description'] },
];

/**
 * Component for managing citations in the site content
 */
const CitationManager = ({ textFields, onInsertCitation }) => {
  // Context hooks
  const { citations, addCitation, updateCitation, deleteCitation } = useCreateSite(); // Get citations from context
  const toast = useToast(); // Initialize toast

  // State for the currently edited citation
  const [editCitation, setEditCitation] = useState(null);
  // State for the citation being edited in modal
  const [formData, setFormData] = useState({});
  // State for form validation
  const [errors, setErrors] = useState({});
  // State for expanded citations
  const [expandedCitations, setExpandedCitations] = useState({}); // { [citationNumber]: boolean }
  // Modal controls
  const { isOpen, onOpen, onClose } = useDisclosure();
  const titleInputRef = useRef(null); // Specifically for the title input
  const modalTriggerRef = useRef(null); // Ref for the element that triggered the modal

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Field theme colors
  const fieldBorderColor = useColorModeValue('gray.300', 'gray.600');
  
  // Handle adding a new citation
  const handleAddCitation = useCallback((event) => { // Added event to get trigger
    modalTriggerRef.current = event.currentTarget; // Store the triggering element
    // Create a new citation with a number one higher than the highest existing citation number
    const highestNumber = citations.reduce((max, citation) =>
      citation.number > max ? citation.number : max, 0);
    
    setEditCitation(null);
    setFormData({
      number: highestNumber + 1,
      type: 'manual', // Default to manual for new user-added citations
      title: '',
      author: '',
      year: '',
      publisher: '',
      pages: '',
      isbn: '',
      doi: '',
      journal: '',
      volume: '',
      issue: '',
      url: '',
      accessDate: '',
      description: '',
      isManual: true, // Mark as manually added
    });
    setErrors({});
    onOpen();
  }, [citations, onOpen]);

  // Handle editing an existing citation
  const handleEditCitation = useCallback((citation, event) => { // Added event to get trigger
    modalTriggerRef.current = event.currentTarget; // Store the triggering element
    setEditCitation(citation);
    setFormData({ ...citation });
    setErrors({});
    onOpen();
  }, [onOpen]);

  // Handle deleting a citation using context function
  const handleDeleteCitation = useCallback((number) => {
    // Consider adding a confirmation dialog here if not already handled by Chakra's IconButton + Tooltip pattern
    // For now, directly calling deleteCitation which handles toast.
    deleteCitation(number);
  }, [deleteCitation]);

  // Handle form field changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: null }));
    }
  }, [errors]);

  // Handle onBlur for specific fields (DOI, ISBN)
  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    // Only validate on blur if the field has content, to avoid premature errors on empty optional fields
    if ((name === 'doi' || name === 'isbn') && value.trim() !== '') {
      validateField(name); // Validate only the blurred field
    }
  }, []);
  
  // Individual field validation - extracted to solve circular dependency
  const validateField = useCallback((fieldName) => {
    const newErrors = { ...errors };
    
    switch (fieldName) {
      case 'title':
        if (!formData.title?.trim()) newErrors.title = 'Title is required';
        else newErrors.title = null;
        break;
      case 'author':
        if (formData.type !== 'other' && !formData.author?.trim()) newErrors.author = 'Author is required';
        else newErrors.author = null;
        break;
      case 'url':
        if (formData.type === 'website') {
          if (!formData.url?.trim()) {
            newErrors.url = 'URL is required for website type.';
          } else {
            try {
              new URL(formData.url); // Basic URL format check
              newErrors.url = null;
            } catch (_) {
              newErrors.url = 'Invalid URL format.';
            }
          }
        } else {
          newErrors.url = null; // Clear if not website type or not required
        }
        break;
      case 'doi':
        if (formData.type === 'article' && formData.doi?.trim()) { // Check if DOI is present and not just whitespace
          if (!/^10\.\d{4,9}\/[-._;()/:A-Z0-9]+$/i.test(formData.doi.trim())) {
            newErrors.doi = 'Invalid DOI format. Example: 10.1234/abc.def';
          } else {
            newErrors.doi = null;
          }
        } else {
          newErrors.doi = null; // Optional field, no error if empty
        }
        break;
      case 'isbn':
        if (formData.type === 'book' && formData.isbn?.trim()) { // Check if ISBN is present and not just whitespace
          const cleanedIsbn = formData.isbn.replace(/-/g, '').trim();
          const isbn10Regex = /^(?:\d{9}[\dXx])$/;
          const isbn13Regex = /^(?:978|979)\d{10}$/;
          if (cleanedIsbn && !isbn10Regex.test(cleanedIsbn) && !isbn13Regex.test(cleanedIsbn)) {
            newErrors.isbn = 'Invalid ISBN. Must be 10 or 13 digits. Hyphens are allowed.';
          } else {
            newErrors.isbn = null;
          }
        } else {
          newErrors.isbn = null; // Optional field, no error if empty
        }
        break;
      case 'type':
         if (!citationTypes.find(t => t.value === formData.type)) newErrors.type = 'Please select a valid citation type';
         else newErrors.type = null;
        break;
      default:
        break;
    }
    
    setErrors(prevErrors => ({ ...prevErrors, ...newErrors }));
    return !newErrors[fieldName]; // Return whether the field is valid
  }, [formData, errors]);

  // Handle citation type change
  const handleTypeChange = useCallback((e) => {
    const newType = e.target.value;
    setFormData(prev => {
      // Always preserve common fields like number, title, url
      const commonFields = { 
        number: prev.number, 
        type: newType,
        // Always preserve title and url regardless of type - these are universal
        title: prev.title || '',
        url: prev.url || ''  
      };
      
      const typeConfig = citationTypes.find(t => t.value === newType);
      
      // Get the fields for the new type
      const newTypeFields = {};
      if (typeConfig) {
        typeConfig.fields.forEach(field => {
          // Preserve existing values when switching types if the field exists
          newTypeFields[field] = prev[field] !== undefined ? prev[field] : '';
        });
      }
      
      return { ...commonFields, ...newTypeFields };
    });
  }, []);

  // Form validation - can optionally take a field name to validate only that field (e.g., onBlur)
  const validateForm = useCallback((specificField = null) => {
    let isValid = true;
    
    if (specificField) {
      // Validate only a specific field
      isValid = validateField(specificField);
      return isValid;
    } else {
      // Full form validation
      const typeConfig = citationTypes.find(t => t.value === formData.type);
      if (!typeConfig) {
        setErrors(prevErrors => ({ ...prevErrors, type: 'Please select a valid citation type' }));
        return false;
      } else {
        // Validate all relevant fields for the current type
        const fieldResults = ['title', 'author', 'url', 'doi', 'isbn', 'type'].map(field => validateField(field));
        return fieldResults.every(isValid => isValid);
      }
    }
  }, [formData, validateField]); // validateField is needed since it's called within this callback

  // Handle form submission
  const handleSubmit = useCallback(() => {
    if (validateForm()) {
      if (editCitation) {
        // Update existing citation
        const updatedCitations = citations.map(c => 
          c.number === editCitation.number ? formData : c
        );
        // Use context function to update
        updateCitation(editCitation.number, formData);
      } else {
        // Use context function to add
        addCitation(formData);
      }
      onClose();
      // Toast notifications are handled within the context functions now
    }
  }, [validateForm, editCitation, formData, addCitation, updateCitation, onClose, citations]); // Added citations to ensure latest state is used

  // Handle inserting a citation into active text field
  const handleInsertCitationClick = (number) => {
    if (onInsertCitation) {
      onInsertCitation(number);
    }
  };

  // Find citation number references in all text fields
  const usedCitationNumbers = useMemo(() => {
    const used = new Set();
    if (!textFields || typeof textFields !== 'object') return used;

    Object.values(textFields).forEach(text => {
      if (typeof text === 'string') {
        const markers = detectCitationMarkers(text);
        markers.forEach(marker => {
          used.add(String(marker.citationNumber)); // Ensure string comparison later
        });
      }
    });
    return used;
  }, [textFields]); // Only depends on textFields content

  const toggleExpandCitation = (number) => {
    setExpandedCitations(prev => ({
      ...prev,
      [number]: !prev[number]
    }));
  };

  // Effect to set initial focus in modal when it opens
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      setTimeout(() => titleInputRef.current.focus(), 100); // Timeout helps ensure modal is fully rendered
    }
  }, [isOpen]);

  return (
    <Box>
      {/* Citation usage guide */}
      <Alert status="info" variant="left-accent" mb={4} fontSize="sm">
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">How to use citations:</Text>
          <Text fontSize="xs">1. Add sources using the button below</Text>
          <Text fontSize="xs">2. Insert them into your text using the [number] format</Text>
          <Text fontSize="xs">3. Each citation will appear in the references section of your site</Text>
        </VStack>
      </Alert>
      
      {/* Main citation list */}
      <VStack spacing={4} align="stretch" mb={4}>
        {citations.length === 0 ? (
          <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor} bg={bgColor}>
            <Text>No citations added yet. Add your first citation to get started.</Text>
          </Box>
        ) : (
          <VStack spacing={2} align="stretch">
            <Heading as="h4" size="sm" mb={2}>
              Citations ({citations.length})
            </Heading>
            {citations.map((citation) => {
              const isUsed = usedCitationNumbers.has(String(citation.number));
              const CiteIcon = citationTypes.find(t => t.value === citation.type)?.icon || BookOpen;
              
              const isExpanded = !!expandedCitations[citation.number];
              const panelId = `citation-details-panel-${citation.number}`;
              return (
                <Box
                  key={citation.number}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={isUsed ? 'green.300' : borderColor}
                  bg={bgColor}
                  position="relative"
                  data-citation-number={String(citation.number)}
                >
                  <HStack justify="space-between" align="center">
                    <HStack flex="1" minWidth="0"> {/* Ensure HStack can shrink */}
                      <Badge colorScheme="blue" fontSize="md" mr={2} flexShrink={0}>
                        [{citation.number}]
                      </Badge>
                      <Tooltip label={`Type: ${citation.type || 'unknown'}`}>
                        <Box as="span" aria-label={`Citation type: ${citation.type || 'unknown'}`} flexShrink={0}>
                          <CiteIcon size={18} />
                        </Box>
                      </Tooltip>
                      <Text fontWeight="medium" noOfLines={1} title={citation.title} flex="1"> {/* Allow text to take space and truncate */}
                        {citation.title}
                      </Text>
                      {citation.author && (
                        <Text fontSize="sm" color="gray.500" noOfLines={1} title={citation.author} ml={2} display={{ base: 'none', md: 'inline' }}>
                          - {citation.author}
                        </Text>
                      )}
                    </HStack>
                    <HStack spacing={1} flexShrink={0}>
                       <Tooltip label={isExpanded ? "Collapse details" : "Expand details"}>
                        <IconButton
                          size="sm"
                          aria-label={isExpanded ? "Collapse citation details" : "Expand citation details"}
                          aria-expanded={isExpanded}
                          aria-controls={panelId}
                          icon={isExpanded ? <CaretUp /> : <CaretDown />}
                          onClick={() => toggleExpandCitation(citation.number)}
                          variant="ghost"
                        />
                      </Tooltip>
                      <Tooltip label="Insert citation at cursor">
                        <IconButton
                          size="sm"
                          aria-label={`Insert citation ${citation.number}`}
                          icon={<PlusCircle />}
                          onClick={() => handleInsertCitationClick(citation.number)}
                          colorScheme="green"
                          variant="ghost"
                        />
                      </Tooltip>
                      <Tooltip label="Edit citation">
                        <IconButton
                          size="sm"
                          aria-label={`Edit citation ${citation.number}`}
                          icon={<Pencil />}
                          onClick={(e) => handleEditCitation(citation, e)}
                          colorScheme="blue"
                          variant="ghost"
                        />
                      </Tooltip>
                      <Tooltip label="Delete citation">
                        <IconButton
                          size="sm"
                          aria-label={`Delete citation ${citation.number}`}
                          icon={<Trash />}
                          onClick={() => handleDeleteCitation(citation.number)}
                          colorScheme="red"
                          variant="ghost"
                        />
                      </Tooltip>
                    </HStack>
                  </HStack>
                  
                  <Collapse in={isExpanded} animateOpacity>
                    <Box mt={3} pl={2} borderLeftWidth="2px" borderColor="gray.200" id={panelId}>
                      <Text fontSize="sm" color={useColorModeValue("gray.700", "gray.300")}>
                        <strong>Author(s):</strong> {citation.author || 'N/A'}
                      </Text>
                      {citation.year && <Text fontSize="sm"><strong>Year:</strong> {citation.year}</Text>}
                      
                      {citation.type === 'book' && (
                        <>
                          {citation.publisher && <Text fontSize="sm"><strong>Publisher:</strong> {citation.publisher}</Text>}
                          {citation.pages && <Text fontSize="sm"><strong>Pages:</strong> {citation.pages}</Text>}
                          {citation.isbn && (
                            <HStack spacing={1} alignItems="center">
                              <Icon as={Barcode} color="gray.500" />
                              <Text fontSize="sm"><strong>ISBN:</strong> {citation.isbn}</Text>
                            </HStack>
                          )}
                        </>
                      )}

                      {citation.type === 'article' && (
                        <>
                          {citation.journal && <Text fontSize="sm"><strong>Journal:</strong> <em>{citation.journal}</em></Text>}
                          {citation.volume && <Text fontSize="sm"><strong>Volume:</strong> {citation.volume}</Text>}
                          {citation.issue && <Text fontSize="sm"><strong>Issue:</strong> {citation.issue}</Text>}
                          {citation.pages && <Text fontSize="sm"><strong>Pages:</strong> {citation.pages}</Text>}
                          {citation.doi && (
                             <HStack spacing={1} alignItems="center">
                              <Icon as={FileText} color="gray.500" />
                              <Text fontSize="sm"><strong>DOI:</strong> {citation.doi}</Text>
                            </HStack>
                          )}
                        </>
                      )}

                      {citation.type === 'website' && ( // Changed from formData.type to citation.type
                        <>
                          {citation.url && (
                            <HStack spacing={1} alignItems="center">
                               <Icon as={LinkSimple} color="gray.500" />
                               <Text fontSize="sm"><strong>URL:</strong> <a href={citation.url} target="_blank" rel="noopener noreferrer">{citation.url}</a></Text>
                            </HStack>
                          )}
                          {citation.accessDate && <Text fontSize="sm"><strong>Accessed:</strong> {citation.accessDate}</Text>}
                        </>
                      )}
                      {citation.type === 'other' && citation.description && ( // Changed from formData.type to citation.type
                        <Text fontSize="sm"><strong>Description:</strong> {citation.description}</Text>
                      )}
                    </Box>
                  </Collapse>
                  
                  {isUsed && (
                    <Badge
                      position="absolute"
                      top={2}
                      right={isExpanded ? "40px" : "160px"} // Adjusted right offset for more space for buttons
                      colorScheme="green"
                      variant="subtle"
                      zIndex="1"
                    >
                      In use
                    </Badge>
                  )}
                </Box>
              );
            })}
          </VStack>
        )}
        
        <Button
          leftIcon={<PlusCircle />}
          colorScheme="teal"
          onClick={(e) => handleAddCitation(e)}
          alignSelf="flex-start"
          aria-label="Add Citation"
        >
          Add Citation
        </Button>
      </VStack>
      
      {/* Citation edit/add modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" initialFocusRef={titleInputRef} finalFocusRef={modalTriggerRef}>
        <ModalOverlay />
        <ModalContent aria-labelledby="citation-modal-header">
          <ModalHeader id="citation-modal-header">{editCitation ? 'Edit Citation' : 'Add New Citation'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <HStack>
                <FormControl isRequired width="100px">
                  <FormLabel htmlFor="citation-number">Number</FormLabel>
                  <Input
                    id="citation-number"
                    name="number"
                    value={formData.number || ''}
                    onChange={handleChange}
                    type="number"
                    isReadOnly={editCitation !== null}
                  />
                </FormControl>
                
                <FormControl isRequired flex="1">
                  <FormLabel htmlFor="citation-type">Type</FormLabel>
                  <Select
                    id="citation-type"
                    name="type"
                    value={formData.type || 'book'}
                    onChange={handleTypeChange}
                    // ref={initialRef} // Initial focus moved to title or first relevant field
                  >
                    {citationTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              
              <FormControl isRequired isInvalid={!!errors.title}>
                <FormLabel htmlFor="citation-title">Title</FormLabel>
                <Input
                  id="citation-title"
                  name="title"
                  value={formData.title || ''}
                  onChange={handleChange}
                  placeholder="Title of the source"
                  ref={titleInputRef} // Set initial focus here
                />
                {errors.title && <FormHelperText color="red.500">{errors.title}</FormHelperText>}
              </FormControl>
              
              {formData.type !== 'other' && (
                <FormControl isRequired={formData.type !== 'other'} isInvalid={!!errors.author}>
                  <FormLabel htmlFor="citation-author">Author</FormLabel>
                  <Input
                    id="citation-author"
                    name="author"
                    value={formData.author || ''}
                    onChange={handleChange}
                    placeholder="Author name(s)"
                  />
                  {errors.author && <FormHelperText color="red.500">{errors.author}</FormHelperText>}
                </FormControl>
              )}
              
              {(formData.type === 'book' || formData.type === 'article') && (
                <>
                  <FormControl>
                    <FormLabel htmlFor="citation-year">Year</FormLabel>
                    <Input
                      id="citation-year"
                      name="year"
                      value={formData.year || ''}
                      onChange={handleChange}
                      placeholder="Publication year"
                    />
                  </FormControl>
                  
                  {formData.type === 'book' && (
                    <>
                    <FormControl>
                      <FormLabel htmlFor="citation-publisher">Publisher</FormLabel>
                      <Input
                        id="citation-publisher"
                        name="publisher"
                        value={formData.publisher || ''}
                        onChange={handleChange}
                        placeholder="Publisher name"
                      />
                   </FormControl>
                   {/* ISBN Field for Book */}
                    <FormControl>
                      <FormLabel htmlFor="citation-isbn">ISBN</FormLabel>
                      <Input
                        id="citation-isbn"
                        name="isbn"
                        value={formData.isbn || ''}
                        onChange={handleChange}
                        onBlur={handleBlur} // Added onBlur validation
                        placeholder="e.g., 978-3-16-148410-0"
                        isInvalid={!!errors.isbn}
                      />
                      {errors.isbn && <FormHelperText color="red.500">{errors.isbn}</FormHelperText>}
                    </FormControl>
                   </>
                 )}

                 {formData.type === 'article' && (
                    <>
                      <FormControl>
                        <FormLabel htmlFor="citation-journal">Journal</FormLabel>
                        <Input
                          id="citation-journal"
                          name="journal"
                          value={formData.journal || ''}
                          onChange={handleChange}
                          placeholder="Journal name"
                        />
                      </FormControl>
                      <HStack>
                        <FormControl>
                          <FormLabel htmlFor="citation-volume">Volume</FormLabel>
                          <Input
                            id="citation-volume"
                            name="volume"
                            value={formData.volume || ''}
                            onChange={handleChange}
                            placeholder="Volume"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel htmlFor="citation-issue">Issue</FormLabel>
                          <Input
                            id="citation-issue"
                            name="issue"
                            value={formData.issue || ''}
                            onChange={handleChange}
                            placeholder="Issue"
                          />
                        </FormControl>
                      </HStack>
                    </>
                 )}

                 {/* DOI Field for Article */}
                 {formData.type === 'article' && (
                   <FormControl>
                     <FormLabel htmlFor="citation-doi">DOI</FormLabel>
                     <Input
                       id="citation-doi"
                       name="doi"
                       value={formData.doi || ''}
                       onChange={handleChange}
                       onBlur={handleBlur} // Added onBlur validation
                       placeholder="e.g., 10.1000/xyz123"
                       isInvalid={!!errors.doi}
                     />
                     {errors.doi && <FormHelperText color="red.500">{errors.doi}</FormHelperText>}
                   </FormControl>
                 )}

                 <FormControl>
                   <FormLabel htmlFor="citation-pages">Pages</FormLabel>
                   <Input
                     id="citation-pages"
                     name="pages"
                      value={formData.pages || ''}
                      onChange={handleChange}
                      placeholder="Page range (e.g. 123-145)"
                    />
                  </FormControl>
                </>
              )}
              
              {formData.type === 'website' && (
                <>
                  <FormControl isRequired={formData.type === 'website'} isInvalid={!!errors.url}>
                    <FormLabel htmlFor="citation-url">URL</FormLabel>
                    <Input
                      id="citation-url"
                      name="url"
                      value={formData.url || ''}
                      onChange={handleChange}
                      placeholder="https://example.com"
                    />
                    {errors.url && <FormHelperText color="red.500">{errors.url}</FormHelperText>}
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel htmlFor="citation-accessDate">Access Date</FormLabel>
                    <Input
                      id="citation-accessDate"
                      name="accessDate"
                      value={formData.accessDate || ''}
                      onChange={handleChange}
                      placeholder="Date accessed (e.g. June 15, 2024)"
                    />
                  </FormControl>
                </>
              )}
              
              {formData.type === 'other' && (
                <FormControl>
                  <FormLabel htmlFor="citation-description">Description</FormLabel>
                  <Textarea
                    id="citation-description"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    placeholder="Additional information about this source"
                    rows={4}
                  />
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {editCitation ? 'Save Changes' : 'Add Citation'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CitationManager;
